import {GRADIENT_PALETTES} from 'analysis-tool/ui/charting/ChartingTypes';
import {JSONValue} from 'util/Types';

import {
  CONDITIONAL_UI_SCHEMA_KEY,
  DisableFunctionParams,
  EXPANDABLE_DETAILS_KEY,
  FieldUISchemaWithStandardComponent,
  FIELD_DEFAULTS_GENERATOR_KEY,
  FIELD_ORDER_UI_SCHEMA_KEY,
  ON_CHANGE_CONFIG_MIDDLEWARE_KEY,
  ShapeUISchemaNestedShapeValues,
  StepConfigEditorOverride,
} from '../../StepConfigEditorTypes';
import {isSeriesColumnNameNull} from '../StepConfigDisableFieldFunctions';
import {maybeGetValueFromConfig} from '../StepConfigSettingsUtils';
import {
  createAxisConfigGroup,
  createAxisRangeStyleSection,
  createErrorBarsSection,
  createLegendStyleSection,
  createMarkerStyleSection,
  DISCRETE_COLOR_PALETTE_DISPLAY_NAMES,
  DISCRETE_COLOR_PALETTE_SVG_PATHS,
  getDefaultSeriesColorConfig,
  GRADIENT_COLOR_PALETTE_DISPLAY_NAMES,
  GRADIENT_COLOR_PALETTE_SVG_PATHS,
  onChangeColorPaletteMiddleware,
} from './ChartSchemaHelpers';

export function isColorPaletteGradient(config: Record<string, JSONValue>): boolean {
  const value = maybeGetValueFromConfig(config, ['chartConfig', 'parameters', 'colorPalette']);
  return typeof value === 'string' && GRADIENT_PALETTES.has(value);
}

export function getDisabledGradientChoices({config, columns}: DisableFunctionParams): ReadonlySet<string> {
  return isSeriesColumnNameNull({config, columns}) ? GRADIENT_PALETTES : new Set();
}

const DISCRETE_CHOICE_GROUP = 'Discrete Colors';
const GRADIENT_CHOICE_GROUP = 'Continuous Gradients';

const COLOR_PALETTE_FIELD_SETTINGS = {
  defaultValue: 'BENCHLING',
  displayNames: {
    ...DISCRETE_COLOR_PALETTE_DISPLAY_NAMES,
    ...GRADIENT_COLOR_PALETTE_DISPLAY_NAMES,
  },
  svgImagePaths: {
    ...DISCRETE_COLOR_PALETTE_SVG_PATHS,
    ...GRADIENT_COLOR_PALETTE_SVG_PATHS,
  },
  disabledChoicesFn: getDisabledGradientChoices,
  disabledChoicesTooltip: 'Select a series column to use gradient palettes',
  choiceGroups: {
    ...Object.fromEntries(
      Object.keys(DISCRETE_COLOR_PALETTE_DISPLAY_NAMES).map((key) => [key, DISCRETE_CHOICE_GROUP])
    ),
    ...Object.fromEntries(
      Object.keys(GRADIENT_COLOR_PALETTE_DISPLAY_NAMES).map((key) => [key, GRADIENT_CHOICE_GROUP])
    ),
  },
};

// Extracted so OXLINT does not flag a TS2322 from the `[key: string]: ShapeUISchema` index
// signature on ShapeUISchemaNestedShapeValues (Symbol keys are incompatible with string index).
const COLOR_PALETTE_GROUP = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['colorPalette', 'discreteSection'],
  [EXPANDABLE_DETAILS_KEY]: {
    headerMode: 'editor',
    headerField: 'colorPalette',
    visibleWhen: {
      conditionFn: (config: Record<string, JSONValue>): boolean => !isColorPaletteGradient(config),
    },
  },
  [ON_CHANGE_CONFIG_MIDDLEWARE_KEY]: onChangeColorPaletteMiddleware,
  colorPalette: {
    displayName: 'Color palette',
    configShapePath: ['chartConfig', 'parameters', 'colorPalette'],
    fieldSettings: COLOR_PALETTE_FIELD_SETTINGS,
  } as FieldUISchemaWithStandardComponent,
  discreteSection: {
    [CONDITIONAL_UI_SCHEMA_KEY]: {
      conditionFn: (config: Record<string, JSONValue>): boolean => !isColorPaletteGradient(config),
    },
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['seriesColorConfigs'],
    seriesColorConfigs: {
      configShapePath: ['chartConfig', 'parameters', 'seriesColorConfigs'],
      editorOverride: StepConfigEditorOverride.MAPPING_ARRAY,
      fieldSettings: {
        mappingSourcePath: ['chartConfig', 'parameters', 'seriesColumnName'],
        mappingKey: 'series',
        showNoMappingEditor: true,
        noMappingDisplayName: 'Base color',
        noMappingSentinel: '__BENCHLING_NO_SERIES_DEFAULT__',
      },
      elementUISchema: {
        [FIELD_ORDER_UI_SCHEMA_KEY]: ['series', 'color'],
        [FIELD_DEFAULTS_GENERATOR_KEY]: getDefaultSeriesColorConfig,
        series: {
          configShapePath: ['series'],
          disableFn: (): boolean => true,
          editorOverride: StepConfigEditorOverride.STRING_LABEL,
        },
        color: {
          configShapePath: ['color'],
        },
      },
    },
  },
};

/**
 * Scatter Chart configuration sections for the Configure tab
 */
export const SCATTER_CHART_CONFIG_SECTIONS = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['axesSection', 'seriesSection', 'errorBarsSection', 'colorPaletteSection'],
  axesSection: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['xAxisGroup', 'yAxisGroup'],
    xAxisGroup: createAxisConfigGroup('x', {includePointLabel: true}),
    yAxisGroup: createAxisConfigGroup('y'),
  },
  seriesSection: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['seriesColumnName'],
    seriesColumnName: {
      displayName: 'Series',
      configShapePath: ['chartConfig', 'parameters', 'seriesColumnName'],
      fieldSettings: {
        allowSelectNull: true,
        noColumnSelectedText: 'None',
      },
    },
  },
  errorBarsSection: createErrorBarsSection('Error bars'),
  colorPaletteSection: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['colorPaletteGroup'],
    colorPaletteGroup: COLOR_PALETTE_GROUP as unknown as ShapeUISchemaNestedShapeValues,
  },
} as const satisfies ShapeUISchemaNestedShapeValues;

/**
 * Scatter Chart style sections for the Style tab
 */
export const SCATTER_CHART_STYLE_SECTIONS = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['legendSection', 'markerStyleSection', 'xAxisSection', 'yAxisSection'],
  legendSection: createLegendStyleSection(),
  markerStyleSection: createMarkerStyleSection(),
  xAxisSection: createAxisRangeStyleSection('x'),
  yAxisSection: createAxisRangeStyleSection('y', {includesZero: true}),
} as const satisfies ShapeUISchemaNestedShapeValues;
